'use client';

import { useEffect, useRef, useCallback } from 'react';

interface AnswerPageAnalyticsProps {
  pageName: string;
}

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

function sendEvent(eventName: string, params: Record<string, unknown>) {
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    window.gtag('event', eventName, params);
  }
}

export default function AnswerPageAnalytics({ pageName }: AnswerPageAnalyticsProps) {
  const entryTimeRef = useRef<number>(0);
  const scrollDepthFiredRef = useRef<Set<number>>(new Set());

  // Time on page — fire on unmount / page unload
  useEffect(() => {
    entryTimeRef.current = Date.now();

    const handleUnload = () => {
      const seconds = Math.round((Date.now() - entryTimeRef.current) / 1000);
      sendEvent('time_on_page', {
        page_name: pageName,
        time_seconds: seconds,
      });
    };

    window.addEventListener('beforeunload', handleUnload);
    return () => {
      window.removeEventListener('beforeunload', handleUnload);
      // Also fire when component unmounts (SPA navigation)
      const seconds = Math.round((Date.now() - entryTimeRef.current) / 1000);
      sendEvent('time_on_page', {
        page_name: pageName,
        time_seconds: seconds,
      });
    };
  }, [pageName]);

  // Scroll depth — fire at 25%, 50%, 75%, 90%
  useEffect(() => {
    const thresholds = [25, 50, 75, 90];
    const fired = scrollDepthFiredRef.current;

    const handleScroll = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const docHeight =
        document.documentElement.scrollHeight - document.documentElement.clientHeight;
      if (docHeight <= 0) return;

      const scrollPercent = Math.round((scrollTop / docHeight) * 100);

      for (const threshold of thresholds) {
        if (scrollPercent >= threshold && !fired.has(threshold)) {
          fired.add(threshold);
          sendEvent('scroll_depth', {
            page_name: pageName,
            scroll_depth_percent: threshold,
          });
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [pageName]);

  // CTA click tracking — delegate from document
  const handleClick = useCallback(
    (e: MouseEvent) => {
      const target = (e.target as HTMLElement).closest('a');
      if (!target) return;

      const href = target.getAttribute('href') || '';
      const text = target.textContent?.trim() || '';

      // Primary CTA: Start Free button → /free-lesson-screen or /conversion-page
      if (href.includes('free-lesson-screen') || href.includes('conversion')) {
        sendEvent('cta_click', {
          page_name: pageName,
          cta_label: text,
          cta_destination: href,
          cta_type: 'primary',
        });
        return;
      }

      // Secondary CTA: Back to AI guides → /learn
      if (href === '/learn') {
        sendEvent('cta_click', {
          page_name: pageName,
          cta_label: text,
          cta_destination: href,
          cta_type: 'secondary',
        });
        return;
      }

      // Internal content links (amber underlined links within article body)
      if (href.startsWith('/') && href !== '/') {
        sendEvent('cta_click', {
          page_name: pageName,
          cta_label: text,
          cta_destination: href,
          cta_type: 'internal_link',
        });
      }
    },
    [pageName]
  );

  useEffect(() => {
    document.addEventListener('click', handleClick);
    return () => {
      document.removeEventListener('click', handleClick);
    };
  }, [handleClick]);

  return null;
}
